import { useState, useRef } from 'react';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { testimonials, faqs } from '@/mocks/articles';
import Newsletter from '@/pages/home/components/Newsletter';

export default function AboutPage() {
  const [contactStatus, setContactStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [contactError, setContactError] = useState('');
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const contactFormUrl = 'https://readdy.ai/api/form/d9qaui4nlsngrm4lluug';
  const formRef = useRef<HTMLFormElement>(null);

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypot = (form.querySelector('[name="company_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setContactStatus('success');
      form.reset();
      return;
    }

    const formData = new FormData(form);
    formData.delete('company_alt');

    try {
      const response = await fetch(contactFormUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: any = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore parse error */ }

      if (response.ok && parsed?.code === 'OK') {
        setContactStatus('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        setContactError(msg);
        setContactStatus('error');
      }
    } catch {
      setContactError('网络错误，请检查网络连接后重试');
      setContactStatus('error');
    }
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: '第一次的人妻',
            url: 'https://diyiciderenqi.com/',
            description: '专为初为人妻的女性提供实用建议的新婚生活指南平台',
            foundingDate: '2025',
            contactPoint: {
              '@type': 'ContactPoint',
              contactType: 'customer service',
              availableLanguage: ['Chinese'],
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://diyiciderenqi.com/' },
              { '@type': 'ListItem', position: 2, name: '关于我们' },
            ],
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqs.map((faq) => ({
              '@type': 'Question',
              name: faq.question,
              acceptedAnswer: {
                '@type': 'Answer',
                text: faq.answer,
              },
            })),
          }),
        }}
      />

      <main className="pt-16 bg-background-50 min-h-screen">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />

          {/* Brand Introduction */}
          <section className="py-10 md:py-16">
            <div className="flex flex-col lg:flex-row items-center gap-10 md:gap-16">
              <div className="flex-1">
                <h1 className="text-3xl md:text-4xl font-heading font-semibold text-foreground-900 mb-5">
                  关于第一次的人妻
                </h1>
                <div className="space-y-4 text-foreground-700 leading-relaxed text-sm md:text-base">
                  <p>
                    <strong className="text-foreground-900">第一次的人妻</strong>是一个专为初为人妻的女性打造的温暖内容平台。我们深知，从恋爱到结婚，从女友到妻子，这段旅程充满了甜蜜，也伴随着不少困惑和挑战。
                  </p>
                  <p>
                    我们的使命是通过优质、实用、有温度的内容，帮助每一位新婚女性从容地完成角色转变，建立健康的夫妻沟通模式，经营幸福的家庭生活，同时在婚姻中保持独立和成长。
                  </p>
                  <p>
                    网站内容涵盖<strong className="text-foreground-900">婚后角色转变、夫妻相处技巧、婚姻经营秘诀、新婚适应期攻略、家庭经营之道、自我成长</strong>六大主题板块，由资深编辑和婚姻关系研究团队精心打磨每一篇文章。
                  </p>
                  <p>
                    我们相信，第一次成为人妻不应该是一场孤军奋战的旅程。在这里，你会发现你并不孤单——你所有的困惑、焦虑和期待，都是被理解和被接纳的。让我们一起，把"第一次"变成最美好的开始。
                  </p>
                </div>

                <div className="grid grid-cols-3 gap-4 md:gap-6 mt-8 md:mt-10">
                  <div className="text-center p-4 rounded-xl bg-accent-50">
                    <div className="text-2xl md:text-3xl font-heading font-bold text-accent-500 mb-1">16+</div>
                    <div className="text-xs md:text-sm text-foreground-600">原创文章</div>
                  </div>
                  <div className="text-center p-4 rounded-xl bg-primary-50">
                    <div className="text-2xl md:text-3xl font-heading font-bold text-primary-500 mb-1">6</div>
                    <div className="text-xs md:text-sm text-foreground-600">主题分类</div>
                  </div>
                  <div className="text-center p-4 rounded-xl bg-secondary-50">
                    <div className="text-2xl md:text-3xl font-heading font-bold text-secondary-500 mb-1">10K+</div>
                    <div className="text-xs md:text-sm text-foreground-600">每月读者</div>
                  </div>
                </div>
              </div>

              <div className="flex-shrink-0 w-full lg:w-[420px]">
                <div className="relative rounded-2xl overflow-hidden aspect-[4/3]">
                  <img
                    src="https://readdy.ai/api/search-image?query=Warm%20cozy%20reading%20nook%20with%20a%20cup%20of%20tea%20and%20an%20open%20book%2C%20soft%20natural%20light%20from%20window%2C%20cream%20and%20blush%20pink%20throw%20blanket%2C%20peaceful%20feminine%20space%2C%20minimalist%20aesthetic%2C%20serene%20atmosphere%2C%20editorial%20photography&width=800&height=600&seq=about-brand-image&orientation=landscape"
                    alt="第一次的人妻 - 品牌形象"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Testimonials */}
          <section id="testimonials" className="py-14 md:py-20 bg-background-100 -mx-4 md:-mx-8 px-4 md:px-8">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-10 md:mb-14">
                <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-900 mb-3">
                  读者的声音
                </h2>
                <p className="text-foreground-600 text-sm md:text-base max-w-lg mx-auto">
                  来自真实读者的反馈，这是我们坚持做下去的最大动力
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
                {testimonials.map((testimonial) => (
                  <div
                    key={testimonial.id}
                    className="bg-white rounded-xl p-5 md:p-6 border border-background-200/70"
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 md:w-11 md:h-11 rounded-full bg-accent-100 flex items-center justify-center">
                        <i className={`${testimonial.avatar} text-accent-500 text-lg`}></i>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-foreground-900">{testimonial.name}</div>
                        <div className="text-xs text-foreground-500">{testimonial.role}</div>
                      </div>
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed">
                      {testimonial.content}
                    </p>
                    <div className="flex items-center gap-1 mt-3">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <i key={star} className="ri-star-fill text-accent-400 text-xs"></i>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Contact Form */}
          <section id="contact" className="py-14 md:py-20">
            <div className="max-w-2xl mx-auto">
              <div className="text-center mb-8 md:mb-10">
                <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-900 mb-3">
                  联系我们
                </h2>
                <p className="text-foreground-600 text-sm md:text-base">
                  有任何问题、建议或合作意向，欢迎随时与我们联系
                </p>
              </div>

              {contactStatus === 'success' ? (
                <div className="text-center bg-accent-100 rounded-2xl p-8 md:p-10">
                  <div className="w-14 h-14 md:w-16 md:h-16 mx-auto mb-4 rounded-full bg-accent-200 flex items-center justify-center">
                    <i className="ri-check-line text-2xl text-accent-600"></i>
                  </div>
                  <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-2">消息已发送</h3>
                  <p className="text-sm text-foreground-600">感谢你的来信，我们会在1-2个工作日内回复你。</p>
                </div>
              ) : (
                <form
                  ref={formRef}
                  onSubmit={handleContactSubmit}
                  data-readdy-form
                  id="contact-form"
                  className="space-y-5 bg-white rounded-2xl p-6 md:p-8 border border-background-200/70"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        姓名 <span className="text-accent-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                        placeholder="请输入你的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        邮箱 <span className="text-accent-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                        placeholder="请输入你的邮箱"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      主题
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                      placeholder="请输入主题"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      留言内容 <span className="text-accent-500">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={5}
                      maxLength={500}
                      className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all resize-none"
                      placeholder="请输入你想说的话（限500字）"
                    ></textarea>
                    <p className="text-xs text-foreground-500 mt-1">最多500个字符</p>
                  </div>

                  <input
                    type="text"
                    name="company_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="honeypot-field"
                  />

                  <button
                    type="submit"
                    className="w-full py-3 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
                  >
                    发送消息
                  </button>

                  {contactStatus === 'error' && (
                    <p className="text-sm text-red-500 text-center">{contactError}</p>
                  )}
                </form>
              )}
            </div>
          </section>

          {/* FAQ */}
          <section className="py-14 md:py-20 bg-background-100 -mx-4 md:-mx-8 px-4 md:px-8">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-10 md:mb-14">
                <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-900 mb-3">
                  常见问题
                </h2>
                <p className="text-foreground-600 text-sm md:text-base">
                  关于第一次的人妻，你可能想了解的信息
                </p>
              </div>

              <div className="space-y-3">
                {faqs.map((faq, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-xl border border-background-200/70 overflow-hidden"
                  >
                    <button
                      onClick={() => setOpenFaq(openFaq === index ? null : index)}
                      className="w-full flex items-center justify-between p-4 md:p-5 text-left cursor-pointer"
                    >
                      <h3 className="text-sm md:text-base font-medium text-foreground-900 pr-4">
                        {faq.question}
                      </h3>
                      <i
                        className={`ri-${openFaq === index ? 'subtract' : 'add'}-line text-foreground-500 text-lg flex-shrink-0 transition-transform duration-200`}
                      ></i>
                    </button>
                    {openFaq === index && (
                      <div className="px-4 md:px-5 pb-4 md:pb-5">
                        <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>
        </div>

        <Newsletter />
      </main>
    </>
  );
}